import { useEffect, useMemo, useState } from 'preact/hooks';

import { captureViewport } from '../capture/screenshot';
import { clearDraft, loadDraft, saveDraft } from '../drafts';
import { t } from '../i18n';
import { createPushLayout, PUSH_MIN_VIEWPORT, SIDEBAR_WIDTH } from '../layout/push';
import { forgetReporter, loadReporters, saveReporter } from '../reporter';
import { captureCount, insertCapture, toStory } from '../story';
import { Icon, initials } from './icons';
import { IdentityForm } from './IdentityForm';
import { HoppingerLogo } from './Logo';
import { MyFeedback } from './MyFeedback';
import { emptyDraft, NewFeedback } from './NewFeedback';
import { AreaPicker, ElementPicker } from './Pickers';
import { Pins } from './Pins';
import { SentView } from './Sent';
import { Thread } from './Thread';

import type { ApiClient } from '../api';
import type { Collector } from '../context/collector';
import type { InsertPoint } from '../story';
import type { Capture, FeedbackItem, Reporter, Selection } from '../types';
import type { Draft } from './NewFeedback';
import type { CaptureTool } from './StoryEditor';

export interface AppProps {
  api: ApiClient;
  collector: Collector;
  host: HTMLElement;
  launcher: boolean;
  side: 'right' | 'left';
  project: string;
  pins: boolean;
  privacyUrl?: string;
  registerOpen: (fn: () => void) => void;
}

type View = 'new' | 'mine' | 'sent' | 'thread';
type Mode = 'idle' | 'element' | 'area' | 'capturing';

const UNREAD_POLL_MS = 60_000;
const NO_INSERT_POINT: InsertPoint = { blockId: null, sourceHtml: '', before: '', after: '' };

/** Waits until the browser has painted with overlays hidden, so they are not in the screenshot. */
const afterPaint = () =>
  new Promise<void>((resolve) =>
    requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
  );

const hasContent = (d: Draft) =>
  Boolean(d.title.trim()) ||
  d.blocks.some((b) => b.kind === 'capture' || b.html.replace(/<[^>]*>|&nbsp;/g, '').trim());

export interface SentSummary {
  item: FeedbackItem;
  screenshots: number;
  path: string;
}

export function App(props: AppProps) {
  const { api, collector, host, launcher, side, project, registerOpen } = props;
  const [open, setOpen] = useState(false);
  const [known, setKnown] = useState<Reporter[]>(loadReporters);
  const [reporter, setReporter] = useState<Reporter | null>(() => known[0] ?? null);
  const [editingReporter, setEditingReporter] = useState(false);
  const [view, setView] = useState<View>('new');
  const [threadId, setThreadId] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('idle');
  const [activeTool, setActiveTool] = useState<CaptureTool | null>(null);
  const [draft, setDraft] = useState<Draft>(() => loadDraft(project) ?? emptyDraft());
  const [draftSaved, setDraftSaved] = useState(false);
  const [captureError, setCaptureError] = useState<string | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [insertAt, setInsertAt] = useState<InsertPoint | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [sent, setSent] = useState<SentSummary | null>(null);
  const [unread, setUnread] = useState(0);
  const [showPins, setShowPins] = useState(false);
  const [pinsVersion, setPinsVersion] = useState(0);
  /** True while the sidebar is docked and the page is pushed aside (wide viewports only). */
  const [pushing, setPushing] = useState(false);
  const layout = useMemo(
    () => createPushLayout({ width: SIDEBAR_WIDTH, side, exclude: host }),
    [side, host],
  );

  useEffect(() => registerOpen(() => setOpen(true)), []);

  // Keep a half-written report across reloads, and say so.
  useEffect(() => {
    setDraftSaved(false);
    const timer = setTimeout(() => {
      saveDraft(project, draft);
      setDraftSaved(hasContent(draft));
    }, 500);
    return () => clearTimeout(timer);
  }, [draft, project]);

  // Replies from the team show as a dot on the tab, also while the sidebar is closed.
  const refreshUnread = () => {
    if (!reporter) return;
    api
      .unread(reporter.email)
      .then(({ unread: count, name }) => {
        setUnread(count);
        // The team corrected this tester's name: remember it here too.
        if (name && name !== reporter.name) {
          const next = { ...reporter, name };
          setKnown(saveReporter(next));
          setReporter(next);
        }
      })
      .catch(() => {});
  };
  useEffect(() => {
    if (!reporter) {
      setUnread(0);
      return;
    }
    refreshUnread();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') refreshUnread();
    }, UNREAD_POLL_MS);
    return () => clearInterval(timer);
  }, [reporter?.email]);

  // Push the page while the sidebar is open on a wide viewport; overlay it on narrow ones.
  useEffect(() => {
    if (!open) return;
    refreshUnread();
    const wide = window.matchMedia(`(min-width: ${PUSH_MIN_VIEWPORT}px)`);
    const apply = () => {
      if (wide.matches) layout.open();
      else layout.close();
      setPushing(wide.matches);
    };
    apply();
    wide.addEventListener('change', apply);
    return () => {
      wide.removeEventListener('change', apply);
      layout.close();
      setPushing(false);
    };
  }, [open, layout]);

  const addToStory = (at: InsertPoint, capture: Capture) => {
    setDraft((d) => {
      const result = insertCapture(d.blocks, at, capture);
      setFocusId(result.focusId);
      return { ...d, blocks: result.blocks };
    });
  };

  // A docked sidebar stays open while picking; an overlay would cover the page, so it hides.
  const startCapture = (tool: CaptureTool, at: InsertPoint) => {
    setInsertAt(at);
    setActiveTool(tool);
    setCaptureError(null);
    if (tool === 'screenshot') {
      void capture(null, at);
      return;
    }
    if (!pushing) setOpen(false);
    setMode(tool);
  };

  const capture = async (selection: Selection | null, at: InsertPoint | null = insertAt) => {
    if (!pushing) setOpen(false);
    setMode('capturing');
    await afterPaint();
    let screenshot: string | null = null;
    try {
      screenshot = await captureViewport({
        exclude: host,
        highlight: selection?.rect,
        reserved: pushing ? { side, width: SIDEBAR_WIDTH } : undefined,
      });
    } catch (err) {
      console.warn('[feedback] screenshot failed', err);
      setCaptureError(t().screenshotFailed);
    }
    if (screenshot || selection) addToStory(at ?? NO_INSERT_POINT, { selection, screenshot });
    setInsertAt(null);
    setActiveTool(null);
    setMode('idle');
    setOpen(true);
  };

  /** Takes a failed screenshot again; an element is looked up again in case it moved. */
  const retryCapture = async (blockId: string) => {
    const block = draft.blocks.find((b) => b.id === blockId);
    if (!block || block.kind !== 'capture') return;
    const selection = block.capture.selection;
    let rect = selection?.rect;
    if (selection?.kind === 'element') {
      try {
        const el = document.querySelector(selection.selector);
        const r = el?.getBoundingClientRect();
        if (r) rect = { x: r.left, y: r.top, width: r.width, height: r.height };
      } catch {
        // Keep the recorded position.
      }
    }
    setRetryingId(blockId);
    try {
      const screenshot = await captureViewport({
        exclude: host,
        highlight: rect,
        reserved: pushing ? { side, width: SIDEBAR_WIDTH } : undefined,
      });
      setDraft((d) => ({
        ...d,
        blocks: d.blocks.map((b) =>
          b.id === blockId && b.kind === 'capture'
            ? { ...b, capture: { ...b.capture, screenshot } }
            : b,
        ),
      }));
      setCaptureError(null);
    } catch {
      setCaptureError(t().screenshotFailed);
    } finally {
      setRetryingId(null);
    }
  };

  const cancelPicking = () => {
    setInsertAt(null);
    setActiveTool(null);
    setMode('idle');
    setOpen(true);
  };

  const submit = async () => {
    if (!reporter) return;
    const story = toStory(draft.blocks);
    const item = await api.submit({
      type: draft.type,
      title: draft.title.trim(),
      story,
      reporter,
      pageUrl: location.href,
      context: collector.snapshot(),
    });
    setSent({ item, screenshots: captureCount(draft.blocks), path: location.pathname });
    setDraft(emptyDraft());
    clearDraft(project);
    setCaptureError(null);
    setPinsVersion((v) => v + 1);
    setView('sent');
  };

  const openThread = (id: string) => {
    setThreadId(id);
    setView('thread');
    setOpen(true);
  };

  const s = t();
  const needsIdentity = !reporter || editingReporter;
  const layerStyle = pushing ? { [side]: SIDEBAR_WIDTH } : {};
  const pinsVisible = props.pins && showPins && open && pushing && mode === 'idle';
  const minePage = view === 'mine' || view === 'thread';

  return (
    <div class={`hfb-root hfb-side-${side}`}>
      {pinsVisible && (
        <Pins
          api={api}
          email={reporter?.email ?? null}
          visibleWidth={document.documentElement.clientWidth - SIDEBAR_WIDTH}
          offsetLeft={side === 'left' ? SIDEBAR_WIDTH : 0}
          onOpenOwn={openThread}
          refreshKey={pinsVersion}
        />
      )}
      {mode === 'element' && (
        <ElementPicker
          host={host}
          onPick={(selection) => void capture(selection)}
          onCancel={cancelPicking}
          layerStyle={layerStyle}
        />
      )}
      {mode === 'area' && (
        <AreaPicker
          onPick={(selection) => void capture(selection)}
          onCancel={cancelPicking}
          layerStyle={layerStyle}
        />
      )}

      {open && (pushing || mode === 'idle') && (
        <aside
          class={`hfb-panel${pushing ? ' is-docked' : ''}`}
          style={{ width: pushing ? SIDEBAR_WIDTH : undefined }}
          aria-label={s.feedback}
        >
          <header class="hfb-header">
            <div class="hfb-brand">
              <HoppingerLogo />
              <span class="hfb-brand-sep" aria-hidden="true" />
              <span class="hfb-brand-title">{s.feedback}</span>
            </div>
            <button
              type="button"
              class="hfb-icon-btn"
              aria-label={s.close}
              onClick={() => setOpen(false)}
            >
              <Icon name="x" size={20} />
            </button>
          </header>

          {!needsIdentity && (
            <nav class="hfb-tabs">
              <button
                type="button"
                class={!minePage ? 'is-active' : ''}
                aria-current={!minePage ? 'page' : undefined}
                onClick={() => setView('new')}
              >
                {s.tabNew}
              </button>
              <button
                type="button"
                class={minePage ? 'is-active' : ''}
                aria-current={minePage ? 'page' : undefined}
                onClick={() => setView('mine')}
              >
                {s.tabMine}
                {unread > 0 && (
                  <span class="hfb-count" aria-label={s.unreadReplies(unread)}>
                    {unread}
                  </span>
                )}
              </button>
              {props.pins && pushing && (
                <label class="hfb-switch">
                  <Icon name="pin" />
                  <span>{s.pins}</span>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={showPins}
                    onChange={(e) => setShowPins(e.currentTarget.checked)}
                  />
                  <span class="hfb-switch-track" aria-hidden="true" />
                </label>
              )}
            </nav>
          )}

          <div class={`hfb-body${view === 'thread' && !needsIdentity ? ' is-thread' : ''}`}>
            {needsIdentity ? (
              <IdentityForm
                known={known}
                privacyUrl={props.privacyUrl}
                onCancel={reporter ? () => setEditingReporter(false) : undefined}
                onForget={(email) => {
                  const list = forgetReporter(email);
                  setKnown(list);
                  if (reporter?.email === email) setReporter(list[0] ?? null);
                }}
                onSave={(next) => {
                  setKnown(saveReporter(next));
                  setReporter(next);
                  setEditingReporter(false);
                  setView('new');
                }}
              />
            ) : view === 'new' ? (
              <NewFeedback
                draft={draft}
                onChange={setDraft}
                onCapture={startCapture}
                onCancelPicking={cancelPicking}
                onImage={(at, dataUrl) =>
                  addToStory(at, { selection: null, screenshot: dataUrl, pasted: true })
                }
                onRetry={(id) => void retryCapture(id)}
                retryingId={retryingId}
                onSubmit={submit}
                focusId={focusId}
                insertAt={insertAt}
                captureError={captureError}
                context={collector.snapshot}
                picking={mode === 'idle' ? null : mode}
                activeTool={activeTool}
                draftSaved={draftSaved}
              />
            ) : view === 'sent' && sent ? (
              <SentView
                sent={sent}
                onView={() => openThread(sent.item.id)}
                onMore={() => setView('new')}
              />
            ) : view === 'thread' && threadId && reporter ? (
              <Thread
                api={api}
                email={reporter.email}
                id={threadId}
                onBack={() => setView('mine')}
                onRead={refreshUnread}
              />
            ) : (
              reporter && (
                <MyFeedback
                  api={api}
                  email={reporter.email}
                  onOpen={openThread}
                  onWrite={() => setView('new')}
                />
              )
            )}
          </div>

          {reporter && !needsIdentity && (
            <footer class="hfb-footer">
              <span class="hfb-avatar" aria-hidden="true">
                {initials(reporter.name)}
              </span>
              <span class="hfb-footer-who">
                <b>{reporter.name}</b>
                <span>{reporter.email}</span>
              </span>
              <button type="button" class="hfb-link" onClick={() => setEditingReporter(true)}>
                {s.change}
              </button>
            </footer>
          )}
        </aside>
      )}

      {launcher && !open && mode === 'idle' && (
        <button
          type="button"
          class="hfb-launcher"
          aria-label={unread > 0 ? `${s.feedback}, ${s.unreadReplies(unread)}` : s.feedback}
          onClick={() => setOpen(true)}
        >
          <Icon name="chat" />
          <span class="hfb-launcher-text">{s.feedback}</span>
          {unread > 0 && <span class="hfb-launcher-dot" aria-hidden="true" />}
        </button>
      )}
    </div>
  );
}
